import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  initialProfile,
  pageTitles,
  steps,
} from './checkInData';
import { getCheckInDetails, getUserProfileDtails } from './checkInService';
import CheckInStep from './stages/CheckInStep';
import DocumentsStep from './stages/DocumentsStep';
import OtpStep from './stages/OtpStep';
import ProfileStep from './stages/ProfileStep';
import WelcomeStep from './stages/WelcomeStep';

const STEP_INDEX = {
  WELCOME: 0,
  OTP: 1,
  PROFILE: 2,
  DOCUMENTS: 3,
  CHECK_IN: 4,
};

const LOGIN_REDIRECT_DELAY_MS = 1200;

function normalizeUserProfile(data = {}, currentProfile = {}) {
  const user = data.inmateDetails || {};

  return {
    id: data.id || currentProfile.id || null,
    uuid: data.uuid || currentProfile.uuid || '',
    inmateDetailsId: user.id || currentProfile.inmateDetailsId || 0,
    inmateDetailsUuid: user.uuid || currentProfile.inmateDetailsUuid || '',
    name: user.name || currentProfile.name || '',
    mobile: data.mobile || currentProfile.mobile || '',
    email: user.email || currentProfile.email || '',
    emergencyContactName: user.emergencyContactName || currentProfile.emergencyContactName || '',
    emergencyContactNumber: user.emergencyContactNumber || currentProfile.emergencyContactNumber || '',
    address: user.address || currentProfile.address || '',
    workplaceCollege: user.workplaceCollege || currentProfile.workplaceCollege || '',
    workplaceCollegeLocation: user.workplaceCollegeAddress || currentProfile.workplaceCollegeLocation || '',
    reference1: user.reference1 || currentProfile.reference1 || '',
    reference1Number: user.reference1Number || currentProfile.reference1Number || '',
    reference2: user.reference2 || currentProfile.reference2 || '',
    reference2Number: user.reference2Number || currentProfile.reference2Number || '',
  };
}

function CheckIn() {
  const { id: pgId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const loginRedirectTimerRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [profile, setProfile] = useState(initialProfile);
  // const [requiresOtp, setRequiresOtp] = useState(false);
  const [verifiedOtp, setVerifiedOtp] = useState('');
  const [checkInSetup, setCheckInSetup] = useState({
    propertyName: '...',
    rentCollectionType: 'BOM',
    pgId: null,
  });
  const [isLoadingSetup, setIsLoadingSetup] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [setupError, setSetupError] = useState('');

  const title = pageTitles[activeStep];
  const mobileLength = useMemo(() => profile.mobile.replace(/\D/g, '').length, [profile.mobile]);

  useEffect(() => {
    return () => {
      if(loginRedirectTimerRef.current){
        window.clearTimeout(loginRedirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if(!pgId){
      return;
    }

    setIsLoadingSetup(true);
    setSetupError('');

    getCheckInDetails(pgId).then(({response, error}) => {
      setIsLoadingSetup(false);

      if(error){
        const message = error?.message || 'Unable to load check-in details';
        setSetupError(message);
        enqueueSnackbar(message, {variant: 'error'});
        return;
      }

      const data = response?.data || response || {};

      setCheckInSetup({
        propertyName: data.pgName,
        rentCollectionType: data.rentCollectionType,
        pgId: data.pgId,
        pgDefaults: data.pgDefaults,
      });
    });
  }, [enqueueSnackbar, pgId]);

  const handleProfileChange = (field) => (event) => {
    const value = event.target.value;

    if(field === 'mobile'){
      // setRequiresOtp(false);
      setVerifiedOtp('');
      setProfile({
        ...initialProfile,
        mobile: value,
      });
      return;
    }

    setProfile({
      ...profile,
      [field]: value,
    });
  };

  const goBack = () => {
    if(activeStep === STEP_INDEX.PROFILE){
      setActiveStep(STEP_INDEX.WELCOME);
      return;
    }

    setActiveStep(step => Math.max(step - 1, 0));
  };

  const goNext = () => {
    setActiveStep(step => Math.min(step + 1, steps.length - 1));
  };

  const getUserDetails = useCallback(() => {

    setIsLoadingUser(true);

    getUserProfileDtails(profile.mobile, {otp: verifiedOtp}).then(({response, error}) => {
      setIsLoadingUser(false);
      if(error){
        setProfile(prevProfile => ({
          ...initialProfile,
          mobile: prevProfile.mobile,
        }));
        return;
      }

      const data = response?.data || response || {};

      setProfile(prevProfile => normalizeUserProfile(data, prevProfile));
    });
  }, [profile.mobile, verifiedOtp]);

  useEffect(() => {
    if(STEP_INDEX.PROFILE === activeStep) {
      getUserDetails()
    }
  
    return () => {
      
    }
  }, [activeStep, getUserDetails])
  

  function onWelcomeStageProceed() {
    // setRequiresOtp(true);
    setActiveStep(STEP_INDEX.OTP);
  }

  function handleOtpVerified(otp) {
    setVerifiedOtp(otp);
    setActiveStep(STEP_INDEX.PROFILE);
  }

  function onProfileSubmitSuccess(data) {
    if(data){
      setProfile(prevProfile => normalizeUserProfile(data, prevProfile));
    }

    goNext();
  }

  function resetCheckInFlow() {
    setActiveStep(STEP_INDEX.WELCOME);
    setProfile(initialProfile);
    // setRequiresOtp(false);
    setVerifiedOtp('');
  }

  function handleCheckInSubmitSuccess() {
    resetCheckInFlow();

    if(loginRedirectTimerRef.current){
      window.clearTimeout(loginRedirectTimerRef.current);
    }

    loginRedirectTimerRef.current = window.setTimeout(() => {
      navigate('/signin/', {replace: true});
    }, LOGIN_REDIRECT_DELAY_MS);
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: (theme) => `linear-gradient(180deg, ${theme.palette.primary.light}14 0%, ${theme.palette.grey[100]} 42%, #ffffff 100%)`,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 760,
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 5 },
        }}
      >
        {activeStep > 0 && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
            <IconButton onClick={goBack} size="small" aria-label="Go back">
              <ArrowBackIosNewIcon />
            </IconButton>
            <Typography component="h1" variant="h5" fontWeight={700}>
              {title}
            </Typography>
          </Stack>
        )}

        {activeStep === STEP_INDEX.WELCOME && (
          <WelcomeStep
            mobile={profile.mobile}
            mobileLength={mobileLength}
            propertyName={checkInSetup.propertyName}
            isLoadingUser={isLoadingUser}
            onMobileChange={handleProfileChange('mobile')}
            onProceed={onWelcomeStageProceed}
          />
        )}

        {activeStep === STEP_INDEX.OTP && (
          <OtpStep
            mobile={profile.mobile}
            onVerified={handleOtpVerified}
          />
        )}

        {activeStep === STEP_INDEX.PROFILE && (
          <ProfileStep
            profile={profile}
            otp={verifiedOtp}
            isNewUser={profile.uuid ? false: true}
            onChange={handleProfileChange}
            onSubmitSuccess={onProfileSubmitSuccess}
          />
        )}

        {activeStep === STEP_INDEX.DOCUMENTS && (
          <DocumentsStep
            pgId={pgId}
            tenantUuid={profile.uuid}
            rentCollectionType={checkInSetup.rentCollectionType}
            pgDefaults={checkInSetup.pgDefaults}
            onProceed={goNext}
            onSubmitSuccess={handleCheckInSubmitSuccess}
          />
        )}

        {activeStep === STEP_INDEX.CHECK_IN && (
          <CheckInStep
            pgId={pgId}
            tenantUuid={profile.uuid}
            rentCollectionType={checkInSetup.rentCollectionType}
            pgDefaults={checkInSetup.pgDefaults}
            onSubmitSuccess={handleCheckInSubmitSuccess}
          />
        )}

        {isLoadingSetup && (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading check-in details...</Typography>
          </Stack>
        )}

        {setupError && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {setupError}
          </Alert>
        )}
      </Box>

    </Box>
  );
}

export default CheckIn;
